import { useState } from "react";
import { Camera, RotateCcw, Check, Loader2 } from "lucide-react";
import { useCamera } from "@/components/kyc/useCamera";

function SelfieView({ live, videoRef, preview }) {
  if (live) return <video ref={videoRef} autoPlay playsInline muted className="h-full w-full -scale-x-100 object-cover" data-testid="selfie-video" />;
  if (preview) return <img src={preview} alt="Selfie preview" className="h-full w-full object-cover" />;
  return <div className="grid h-full place-items-center text-neutral-400"><Camera className="h-10 w-10" /></div>;
}

function StartButton({ uploading, retake, onClick }) {
  const Icon = uploading ? Loader2 : retake ? RotateCcw : Camera;
  return (
    <button type="button" disabled={uploading} onClick={onClick} data-testid="selfie-start-btn"
      className="flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm font-semibold transition-colors duration-200 hover:border-[#FF5A00] disabled:opacity-50">
      <Icon className={`h-4 w-4 ${uploading ? "animate-spin" : ""}`} />
      {uploading ? "Uploading…" : retake ? "Retake" : "Open camera"}
    </button>
  );
}

export function SelfieCapture({ onCapture, uploaded, disabled }) {
  const cam = useCamera();
  const [state, setState] = useState("idle");
  const [preview, setPreview] = useState(null);

  const snap = async () => {
    const blob = await cam.capture();
    if (!blob) return;
    cam.stop();
    setPreview(URL.createObjectURL(blob));
    setState("uploading");
    const ok = await onCapture(new File([blob], "selfie.jpg", { type: "image/jpeg" }));
    setState(ok ? "done" : "idle");
  };

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4" data-testid="selfie-capture">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Live selfie</p>
        {(uploaded || state === "done") && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="selfie-uploaded"><Check className="h-4 w-4" />Captured</span>}
      </div>
      <p className="mt-1 text-xs text-neutral-500">Face the camera in good light. No caps or sunglasses.</p>
      <div className="mt-3 aspect-square w-full max-w-[220px] overflow-hidden rounded-2xl bg-neutral-100">
        <SelfieView live={cam.live} videoRef={cam.videoRef} preview={preview} />
      </div>
      {cam.error && <p className="mt-2 text-xs text-rose-600" data-testid="selfie-error">{cam.error}</p>}
      <div className="mt-3 flex gap-2">
        {cam.live && <button type="button" onClick={snap} data-testid="selfie-snap-btn" className="rounded-full bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white">Take photo</button>}
        {!cam.live && !disabled && <StartButton uploading={state === "uploading"} retake={!!(preview || uploaded)} onClick={cam.start} />}
      </div>
    </div>
  );
}
